import { Injectable } from '@angular/core';
import { Claims, AuthenticationService, Token } from '@mitel/cloudlink-sdk/authentication';
import { User } from '@mitel/cloudlink-sdk/admin';
import { SharedHelpers } from '../classes/sharedhelpers';
import { defer, ReplaySubject, Subject, Subscription } from 'rxjs';
import { ChatCloudLinkService } from './chat-cloudlink.service';
import { ChatLoggingService } from './chat-logging.service';
import { ChatDatabaseService } from './chat-database.service';
import { PolicyTestRequest } from '@mitel/cloudlink-sdk/authentication';
import { retryWhen, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ChatAuthService {
  private allowEditDeletePolicy = 'chat.api.mitel.io/2017-09-01/conversations/1234/messages/1234';
  public allowEditDelete = null;

  public offlineLogin = false;

  public ready$: ReplaySubject<boolean>;
  public triggerChangeDetection$?: Subject<void> = new Subject<void>();

  public auth: AuthenticationService;

  /** Current Claims Object */
  public claims: Claims | null;
  /** Current User Object  */
  public user: User | null;
  /** Current Auth Token */
  public token: Token;
  public userId: string;
  public accountId: string;

  constructor(
    private chatCloudlinkService: ChatCloudLinkService,
    private chatLoggingService: ChatLoggingService,
    private chatDatabaseService: ChatDatabaseService
  ) {
    this.loginUser();
  }

  public loginUser() {
    this.userId = null;
    this.offlineLogin = false;
    this.ready$ = new ReplaySubject(1);
    var clSub = new Subscription();
    clSub.add(
      this.chatCloudlinkService.ready$.subscribe(
        async () => {
          clSub.unsubscribe();
          if (this.chatCloudlinkService.jsonMessagesAsSource || this.chatCloudlinkService.aiMode) {
            this.chatLoggingService.info({
              logMessage: 'chat-auth.service.loginUser() jsonMessagesAsSource/aiMode enabled - set ready'
            });
            this.ready$.next(true);
          } else if (!this.chatCloudlinkService.isOffline() || !this.chatDatabaseService.indexDbEnabled) {
            this.getClaims();
          } else {
            this.chatLoggingService.info({
              logMessage: 'chat-auth.service.loginUser() Starting offline'
            });
            let lastLoggedInUser;
            try {
              lastLoggedInUser = await this.chatDatabaseService.getLoggedInUser();
            } catch (ex) {
              this.chatLoggingService.error({
                logMessage: 'chat-auth.service.loginUser() Error getting logged in user from db',
                logParameters: [ex]
              });
            }
            if (lastLoggedInUser) {
              this.userId = lastLoggedInUser.principalId;
              this.accountId = lastLoggedInUser.accountId;
            }
            this.offlineLogin = true;
            this.ready$.next(true);
          }
        },
        er => {
          //Let chat component handle error
        }
      )
    );
  }

  public getClaims(retryCount = 0) {
    this.chatLoggingService.info({
      logMessage: 'chat-auth.service.getClaims()'
    });

    this.auth = SharedHelpers.getSdkConfig().getAuthenticationService();

    const timeoutPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        reject(new Error('Request timed out'));
      }, 30000); // 30 seconds timeout
    });

    Promise.race([this.auth.whoAmI(), timeoutPromise])
      .then((resClaims: any) => {
        const claims: Claims = resClaims?.body;
        // If `whoAmI` resolves before the timeout, handle the claims
        this.chatLoggingService.info({
          logMessage: 'chat-auth.service.getClaims()',
          logParameters: [!!claims]
        });
        if (!!claims) {
          this.claims = claims;
          this.accountId = claims.accountId;
          this.chatCloudlinkService.accountId = claims.accountId;

          this.auth
            .getToken()
            .catch(err => {
              this.chatLoggingService.error({
                logMessage: 'chat-auth.service.getToken(): Error',
                logParameters: [err]
              });
              this.ready$.error('chat-auth.service.getToken()');
            })
            .then(token => {
              if (token) {
                this.token = token;
                if (!this.user) {
                  this.user = this.getUserFromClaims(claims);
                  this.userId = this.user.userId;
                }
                this.ready$.next(true);
                return claims;
              } else {
                this.chatLoggingService.error({
                  logMessage: 'chat-auth.service.getToken(): No Token in Local Store'
                });
                this.ready$.error('chat-auth.service.getToken()');
              }
            });
        } else {
          this.chatLoggingService.error({
            logMessage: 'chat-auth.service.getClaims(): No Claims'
          });
          this.ready$.error('chat-auth.service.getClaims()');
        }
      })
      .catch(err => {
        this.chatLoggingService.error({
          logMessage: 'chat-auth.service.getClaims(): Error - Maybe Not Logged In',
          logParameters: [err]
        });
        // Retry with increasing delay — covers the OAuth callback case where the token
        // isn't stored yet when checkForNotify fires (auth.login still in progress at t=1s)
        if (retryCount < 3) {
          setTimeout(() => this.getClaims(retryCount + 1), 2000 * (retryCount + 1));
        } else {
          this.ready$.error('chat-auth.service.getClaims()');
        }
      });
  }

  public reset() {
    this.ready$ = new ReplaySubject(1);
    this.user = null;
  }

  public async refreshToken(): Promise<Token> {
    const res = await this.auth.refresh();
    this.token = res.body;
    return this.token;
  }

  private getUserFromClaims(claims: Claims): User {
    let user: User = {
      userId: claims.principalId,
      accountId: claims.accountId,
      languageCode: (<any>claims).languageCode || 'en-US'
    };
    return user;
  }

  public async getEditDeletePolicy(): Promise<void> {
    this.ready$.pipe(take(1)).subscribe(
      async () => {
        if (this.chatCloudlinkService.jsonMessagesAsSource || this.chatCloudlinkService.aiMode) {
          this.allowEditDelete = false;
          return;
        }
        if (this.allowEditDelete === null) {
          this.allowEditDelete = false;
          //Only need PUT as accounts wraps both PUT/DELETE to a single toggle
          const policyRequest: PolicyTestRequest = {
            resources: [
              {
                resource: this.allowEditDeletePolicy,
                action: 'PUT'
              }
            ]
          };
          try {
            const resPolicyTestResponse = await defer(() => this.auth.testPolicy(policyRequest))
              .pipe(
                retryWhen(
                  SharedHelpers.genericRetryStrategy(
                    '[chat-auth.service.getEditDeletePolicy()]',
                    this.chatLoggingService
                  )
                )
              )
              .toPromise();
            let policyTestResponse = resPolicyTestResponse.body;
            if (policyTestResponse?.results?.length) {
              let match = policyTestResponse.results.find(r => r.resource === this.allowEditDeletePolicy);
              if (match && (match?.effect === 'ImplicitAllow' || match?.effect === 'ExplicitAllow')) {
                this.allowEditDelete = true;
                this.triggerChangeDetection$.next();
              }
            }
          } catch (ex) {
            this.allowEditDelete = false;
            this.chatLoggingService.error({
              logMessage: 'chat-auth.service.getEditDeletePolicy(): Error - Ignore if jsonMessagesAsSource enabled',
              logParameters: [ex]
            });
          }
        }
      },
      er => {
        //Let chat component handle error
      }
    );
  }
}
